import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from "recharts";
import { Users, KeyRound, Mail, ArrowRight, Loader2 } from "lucide-react";
import client from "../../lib/api";
import { PageHeader, KpiCard, PrivacyBadge } from "./adminUi";

const STATUS_COLORS = { completed: "#0E7A66", in_progress: "#1D5FD4", signed_up: "#8A5808", invited: "rgba(19,24,34,0.35)" };
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

export default function AdminOverviewScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/overview").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="overview-loading"><Loader2 className="animate-spin" size={20} /></div>;
  }
  if (!data) return <div className="k-card" style={{ padding: "2rem" }} data-testid="overview-error">We could not load the overview.</div>;

  const r = data.roster;
  const seats = data.seats;
  const donut = [
    { key: "completed", name: "Completed", value: r.completed },
    { key: "in_progress", name: "In progress", value: r.in_progress },
    { key: "signed_up", name: "Signed up", value: r.signed_up },
    { key: "invited", name: "Invited", value: r.invited },
  ].filter((d) => d.value > 0);

  const funnel = [
    { stage: "Invited", n: r.total },
    { stage: "Signed up", n: r.signed_up_total },
    { stage: "In progress", n: r.in_progress + r.completed },
    { stage: "Completed", n: r.completed },
  ];

  return (
    <div data-testid="admin-overview">
      <PageHeader
        title={`${data.org.display_name} overview`}
        subtitle="Where your cohort is right now: who you have invited, who has signed up, and who has finished. Numbers only, never an individual read."
        actions={<PrivacyBadge />}
      />

      {!data.email_configured && (
        <div className="k-card mb-6 flex items-start gap-3" style={{ padding: "1rem 1.25rem", background: "rgba(138,88,8,0.06)", borderColor: "rgba(138,88,8,0.25)" }} data-testid="overview-email-notice">
          <Mail size={18} style={{ color: "#8A5808", marginTop: 2 }} />
          <div className="text-sm" style={{ color: "#131822" }}>
            Invitation emails are queued but not yet delivered. Add a verified sender in Settings to start sending. Until then every invite is logged so you can test the flow.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="kpi-students" label="Students" value={r.total} sub={`${r.not_signed_up} not signed up yet`} icon={Users} />
        <KpiCard testId="kpi-signed-up" label="Signed up" value={r.signed_up_total} sub={pct(r.signup_rate) + " of invited"} accent="#8A5808" />
        <KpiCard testId="kpi-completed" label="Completed read" value={r.completed} sub={pct(r.completion_rate) + " completion"} accent="#0E7A66" />
        <KpiCard testId="kpi-seats" label="Seats used" value={`${seats.consumed}/${seats.total}`} sub={pct(seats.utilisation) + " utilised"} accent="#1D5FD4" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="k-card lg:col-span-1" style={{ padding: "1.5rem" }} data-testid="overview-status-donut">
          <div className="text-sm font-medium mb-1" style={{ color: "#131822" }}>Cohort status</div>
          <div className="k-quiet !text-xs mb-4">Share of students at each stage</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={donut} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={54} outerRadius={82} paddingAngle={2}>
                {donut.map((d) => <Cell key={d.key} fill={STATUS_COLORS[d.key]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono, monospace", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {donut.map((d) => (
              <div key={d.key} className="flex items-center gap-2 text-xs" style={{ color: "rgba(19,24,34,0.7)" }}>
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: STATUS_COLORS[d.key] }} />
                {d.name} <span className="klareet-mono ml-auto">{d.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="k-card lg:col-span-2" style={{ padding: "1.5rem" }} data-testid="overview-funnel">
          <div className="text-sm font-medium mb-1" style={{ color: "#131822" }}>Activation funnel</div>
          <div className="k-quiet !text-xs mb-4">From invited to a finished read</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 20, right: 24 }}>
              <CartesianGrid horizontal={false} stroke="rgba(19,24,34,0.06)" />
              <XAxis type="number" tick={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "rgba(19,24,34,0.6)" }} />
              <YAxis type="category" dataKey="stage" width={90} tick={{ fontFamily: "Archivo, sans-serif", fontSize: 12, fill: "#131822" }} />
              <Tooltip cursor={{ fill: "rgba(19,24,34,0.03)" }} contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono, monospace", fontSize: 12 }} />
              <Bar dataKey="n" fill="#1D5FD4" radius={[0, 4, 4, 0]} barSize={26} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/admin/roster" className="k-btn k-btn-primary !py-2" data-testid="overview-goto-roster">
              <Users size={15} /> View students <ArrowRight size={14} />
            </Link>
            <Link to="/admin/seats" className="k-btn k-btn-ghost !py-2" data-testid="overview-goto-seats">
              <KeyRound size={15} /> Seat activation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
